// Motore delle colonne configurabili: config JSON (`gestore.dashboard_columns`)
// → column def MUI DataGrid. Generico e senza dipendenze di dominio: i renderer
// specifici (Trenord & co.) arrivano dal consumer come mappa `presets`.

import { DEFAULT_DATETIME_FORMAT } from '@/constants/dashboardColumns';

import AppStatusChip from '../buttons/AppStatusChip';

import {
  applyTemplate,
  formatValue,
  getValueByPath,
  resolveStyle,
  styleToCss,
  toDayjs,
} from './columnFormatters';
import { PassengerCell, PriceCell, RouteCell, WhenCell } from './index.jsx';

/** Contenuto della cella prima dell'applicazione dello stile. */
function renderContent(cfg, row, text) {
  const value = getValueByPath(row, cfg.field);
  switch (cfg.type) {
    case 'currency':
      return <PriceCell amount={value} />;
    case 'passenger':
      return <PassengerCell name={text} />;
    case 'route':
      return <RouteCell from={value} to={getValueByPath(row, cfg.field_to)} />;
    case 'date':
    case 'datetime': {
      const d = toDayjs(value);
      if (!d) return '—';
      const withTime = (cfg.format || DEFAULT_DATETIME_FORMAT).includes('H');
      return (
        <WhenCell
          icon="calendar"
          primary={d.format(cfg.format?.split(' ')[0] || 'DD/MM/YYYY')}
          secondary={cfg.type === 'datetime' || withTime ? d.format('HH:mm') : null}
        />
      );
    }
    default:
      return text;
  }
}

/** Wrappa il contenuto con lo stile risolto (base + prima regola che matcha). */
function withStyle(content, style) {
  const { wrapper, inner } = styleToCss(style);
  if (style.display === 'chip') {
    return (
      <div style={wrapper}>
        <AppStatusChip variant={style.chipVariant || 'neutral'} label={content} fullCell={false} />
      </div>
    );
  }
  return (
    <div style={wrapper}>
      <span style={inner}>{content}</span>
    </div>
  );
}

/**
 * Costruisce la column def di una colonna custom.
 *
 * `presets` è una mappa `chiave → renderCell` iniettata già risolta dal
 * consumer (che vi lega il proprio contesto); con `type: 'preset'` la cella è
 * delegata interamente al renderer, che decide da sé la propria resa.
 */
export function buildConfigurableColumn(cfg, { presets = {} } = {}) {
  const base = {
    field: cfg.field || cfg.id,
    headerName: cfg.headerName || cfg.field || '',
    width: cfg.width || 160,
    sortable: cfg.type !== 'preset' && cfg.type !== 'template',
  };

  if (cfg.type === 'preset') {
    return { ...base, renderCell: presets[cfg.preset] || undefined };
  }

  return {
    ...base,
    renderCell: (params) => {
      const row = params.row || {};
      const raw =
        cfg.type === 'template' ? applyTemplate(cfg.template, row) : getValueByPath(row, cfg.field);
      const text = cfg.type === 'template' ? raw : formatValue(raw, cfg);
      const style = resolveStyle(cfg.style, cfg.rules, raw);
      return withStyle(renderContent(cfg, row, text), style);
    },
  };
}

/**
 * Riconduce allo schema corrente le entry scritte con lo schema legacy
 * (`{ field, headerName, width, pos, renderCell }`, senza `source`/`type`):
 * con un renderer nominato sono colonne preset, altrimenti sono la colonna
 * standard con quel `field` se esiste nel set corrente.
 */
function normalizeEntry(cfg, byField) {
  if (cfg.source) return cfg;
  const preset = cfg.preset || cfg.renderCell;
  if (preset) return { ...cfg, source: 'custom', type: 'preset', preset };
  if (byField.has(cfg.field)) return { ...cfg, source: 'standard' };
  return { ...cfg, source: 'custom', type: cfg.type || 'text' };
}

/** Applica gli override di config (etichetta, larghezza, stile) a una colonna standard. */
function applyStandardOverrides(standardCol, cfg) {
  const col = { ...standardCol };
  if (cfg.headerName) col.headerName = cfg.headerName;
  if (cfg.width) col.width = cfg.width;

  const hasStyle =
    cfg.style && Object.keys(cfg.style).some((k) => cfg.style[k] != null && k !== 'display');
  const hasRules = Array.isArray(cfg.rules) && cfg.rules.length > 0;
  if (!hasStyle && !hasRules) return col;

  const inner = standardCol.renderCell;
  col.renderCell = (params) => {
    const raw = getValueByPath(params.row || {}, cfg.field);
    const style = resolveStyle(cfg.style, cfg.rules, raw);
    const { wrapper } = styleToCss(style);
    return <div style={wrapper}>{inner ? inner(params) : params.value}</div>;
  };
  return col;
}

/**
 * Colonne effettive di una griglia: se il gestore non ha config, le standard
 * invariate; altrimenti la lista configurata (ordinata per `pos`, senza le
 * nascoste), risolvendo le standard per `field` e costruendo le custom.
 */
export function resolveConfiguredColumns(standardColumns, config, options = {}) {
  if (!Array.isArray(config) || config.length === 0) return standardColumns;

  const byField = new Map(standardColumns.map((c) => [c.field, c]));

  return [...config]
    .sort((a, b) => (a.pos ?? 0) - (b.pos ?? 0))
    .filter((c) => !c.hidden)
    .map((entry) => {
      const cfg = normalizeEntry(entry, byField);
      if (cfg.source === 'standard') {
        const standard = byField.get(cfg.field);
        return standard ? applyStandardOverrides(standard, cfg) : null;
      }
      return buildConfigurableColumn(cfg, options);
    })
    .filter(Boolean);
}
